import React, { useState, useEffect } from 'react';

const MetaVerificationsHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-verifications/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'created': return '✨';
      case 'updated': return '📝';
      case 'status_changed': return '🔄';
      case 'comment_added': return '💬';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      case 'escalated': return '🔺';
      case 'verification_started': return '🔍';
      case 'verification_completed': return '✅';
      case 'issue_resolved': return '🛠️';
      case 'priority_changed': return '⚡';
      default: return '📋';
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'created': return 'text-green-600 bg-green-100';
      case 'updated': return 'text-blue-600 bg-blue-100';
      case 'status_changed': return 'text-blue-600 bg-blue-100';
      case 'comment_added': return 'text-gray-600 bg-gray-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'escalated': return 'text-red-600 bg-red-100';
      case 'verification_started': return 'text-blue-600 bg-blue-100';
      case 'verification_completed': return 'text-green-600 bg-green-100';
      case 'issue_resolved': return 'text-green-600 bg-green-100';
      case 'priority_changed': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionDescription = (entry) => {
    switch (entry.action_type) {
      case 'created':
        return 'Verificación Meta creada';
      case 'updated':
        return 'Registro actualizado';
      case 'status_changed':
        return entry.old_status && entry.new_status 
          ? `Estado cambiado de "${entry.old_status}" a "${entry.new_status}"`
          : 'Estado cambiado';
      case 'comment_added':
        return 'Comentario agregado';
      case 'completed':
        return 'Verificación completada';
      case 'cancelled':
        return 'Verificación cancelada';
      case 'escalated':
        return 'Verificación escalada';
      case 'verification_started':
        return 'Verificación iniciada';
      case 'verification_completed':
        return 'Proceso de verificación completado';
      case 'issue_resolved':
        return 'Problema resuelto';
      case 'priority_changed':
        return entry.old_status && entry.new_status 
          ? `Prioridad cambiada de "${entry.old_status}" a "${entry.new_status}"`
          : 'Prioridad cambiada';
      default:
        return entry.action_type;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="mr-3">📋</span>
                Historial de Verificaciones Meta
              </h2>
              <p className="text-blue-100 mt-1">
                Registro completo de todas las acciones realizadas
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-lg text-gray-600">Cargando historial...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No hay historial disponible
              </h3>
              <p className="text-gray-600">
                Las acciones realizadas en esta verificación aparecerán aquí.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Timeline */}
              <div className="relative">
                {history.map((entry, index) => (
                  <div key={entry.id} className="relative flex items-start space-x-4 pb-8">
                    {/* Timeline line */}
                    {index < history.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                    )}
                    
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl ${getActionColor(entry.action_type)}`}>
                      {getActionIcon(entry.action_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {getActionDescription(entry)}
                            </h4>
                            <p className="text-sm text-gray-600">
                              Por: <span className="font-medium">{entry.user_name}</span>
                            </p>
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        {/* Comment */}
                        {entry.comment && (
                          <p className="text-sm text-gray-700 mb-2">
                            {entry.comment}
                          </p>
                        )}
                        
                        {/* Details */}
                        {entry.details && (
                          <div className="text-xs text-gray-600 bg-white rounded p-2">
                            <strong>Detalles:</strong> {entry.details}
                          </div>
                        )}
                        
                        {/* Status changes */}
                        {(entry.old_status || entry.new_status) && (
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            {entry.old_status && (
                              <div className="bg-red-50 p-2 rounded border border-red-200">
                                <span className="font-medium text-gray-700">Estado anterior:</span>
                                <p className="text-red-700">{entry.old_status}</p>
                              </div>
                            )}
                            {entry.new_status && (
                              <div className="bg-green-50 p-2 rounded border border-green-200">
                                <span className="font-medium text-gray-700">Nuevo estado:</span>
                                <p className="text-green-700">{entry.new_status}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen del Historial</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {history.length}
                    </div>
                    <div className="text-sm text-gray-600">Total Acciones</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                      {history.filter(h => h.action_type === 'status_changed').length}
                    </div>
                    <div className="text-sm text-gray-600">Cambios Estado</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {history.filter(h => h.action_type === 'verification_completed').length}
                    </div>
                    <div className="text-sm text-gray-600">Verificaciones</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {new Set(history.map(h => h.user_name)).size}
                    </div>
                    <div className="text-sm text-gray-600">Usuarios</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaVerificationsHistory;
